import { trackAnalyticsGrowthbookEvent } from '@ifixit/analytics';
import { type Experiment, GrowthBook, type Result } from '@growthbook/growthbook';
import { type Branded, getCookieDomain } from '@ifixit/helpers';
import Cookies from 'universal-cookie';
import 'Shared/global.d';
import { safeLocalStorage } from '@ifixit/utils';

const GROWTHBOOK_DEBUG = new URLSearchParams(window.location.search).get('gbDebug') === 'true';

const canGroupLogs = 'group' in console && 'groupCollapsed' in console && 'groupEnd' in console;

const AB_TEST_PARTICIPANT_COOKIE_NAME = 'ab_participant';
type ABTestParticipantId = Branded<string, 'ABTestParticipantId'>;

const logger = canGroupLogs
   ? /* eslint-disable no-console */
     (msg: string, ctx: Record<string, unknown>) => {
        console.groupCollapsed(`[GrowthBook] ${msg}`);
        console.log(ctx);
        console.groupEnd();
     }
   : (msg: string, ctx: Record<string, unknown>) => {
        console.log(`[GrowthBook] ${msg}`, ctx);
     };
/* eslint-enable no-console */

export async function handleServerExperimentViewed() {
   if (!App.gb) {
      // Growthbook is not enabled on this pagetype. Currently only enabled on CartPage and GuidePage.
      return;
   }

   const growthbook = await getGrowthbook();

   if (App.gb.viewedExperiments) {
      growthbook.setDeferredTrackingCalls(App.gb.viewedExperiments);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME: Promises must be awaited, end with...
      growthbook.fireDeferredTrackingCalls();
   }
}

/**
 * @knipignore
 */
export async function isFeatureOn(feature: string) {
   const growthbook = await getGrowthbook();
   return growthbook.isOn(feature);
}

let growthbookInstance: GrowthBook | null = null;

export async function getGrowthbook() {
   return getGrowthbookSync();
}

/**
 * The same singleton as getGrowthbook(), without the await.
 *
 * PHP inlines the whole GrowthBook payload into `App.gb` before the page
 * renders, so nothing here needs the network. `initSync` loads that payload in
 * one synchronous call, the same way the Next.js side does it
 * (apps/nextjs/app/_helpers/growthbook/client.ts), and isOn() then evaluates in
 * memory. Callers that must pick an index before their first render (Carpenter
 * site search, issue #55186) can't await, and awaiting would render the control
 * first and swap under the user.
 */
export function getGrowthbookSync(): GrowthBook {
   if (growthbookInstance) {
      return growthbookInstance;
   }

   const growthbook: GrowthBook = new GrowthBook({
      apiHost: 'https://growthbook-proxy.ifixit.com',
      clientKey: 'sdk-pkazXeoBV1oUMx',
      enableDevMode: GROWTHBOOK_DEBUG,
      debug: GROWTHBOOK_DEBUG,
      backgroundSync: false,
      log: logger,
      attributes: App.gb?.attributes
         ? { uniqueId: getABTestParticipantId(), ...App.gb.attributes }
         : undefined,
      forcedVariations: App.gb?.forcedVariations,
      trackingCallback: (experiment: Experiment<any>, result: Result<any>) => {
         const experimentId = experiment.key;
         const variationId = result.key;

         growthbook.log('Experiment Viewed', {
            experimentId,
            variationId,
            fullDetails: {
               experiment,
               result,
            },
         });

         trackAnalyticsGrowthbookEvent({ experimentId, variationId });
      },
   });

   if (App.gb?.features) {
      growthbook.initSync({ payload: { features: App.gb.features } });
   }

   if (App.gb?.forcedFeatures) {
      growthbook.setForcedFeatures(new Map(Object.entries(App.gb.forcedFeatures)));
   }

   growthbookInstance = growthbook;

   return growthbook;
}

function getABTestParticipantId(): ABTestParticipantId {
   const cookies = new Cookies();
   const existing = cookies.get(AB_TEST_PARTICIPANT_COOKIE_NAME);
   if (existing) return existing as ABTestParticipantId;

   const id = generateABTestParticipantId() as ABTestParticipantId;
   // Set cookie for 30 days on the root domain so the participant ID
   // is stable across locale subdomains (e.g. www. vs de. vs fr.).
   cookies.set(AB_TEST_PARTICIPANT_COOKIE_NAME, id, {
      maxAge: 30 * 24 * 60 * 60,
      path: '/',
      domain: getCookieDomain(window.location),
      // Match the PHP writer's attributes so a visitor carries one
      // ab_participant cookie across stacks. See iFixit/ifixit#62634.
      // Secure is gated on protocol so the cookie still writes on http://localhost.
      sameSite: 'lax',
      secure: window.location.protocol === 'https:',
   });
   return id;
}

/**
 * Generate a random AB test participant ID.
 * Always 11 chars long and looks like: `765zsxykdtk`
 *
 * Chance of collision is: 1 in 36^11 = 1 / 131,621,703,842,267,136
 * For 1 million participants, the chance of collision is about 0.00014%
 */
function generateABTestParticipantId(): string {
   return (
      Math.random()
         // 0-9 and a-z
         .toString(36)
         // Remove the `0.`
         .slice(2)
   );
}

export function AddVercelToolBar() {
   const toolbar = App.toolbar;
   if (!toolbar) {
      return;
   }

   // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME: Promises must be awaited, end with...
   Promise.all([
      import('Shared/react-mount-tracker'),
      import('Shared/react-query-initialize'),
      import('@ifixit/toolbar'),
      import('Shared/toolbar-dev-tools/map-descriptors'),
      import('Shared/toolbar-dev-tools/CarpenterDevTools'),
   ]).then(
      ([
         { createTrackedRoot },
         { initializeReactQuery },
         { default: Toolbar },
         { mapDescriptorsToExtensions, createMetricExtensions, getStoredMetrics, hasMetrics },
         { CarpenterDevTools },
      ]) => {
         const toolbarWrapper = document.createElement('div');
         document.body.append(toolbarWrapper);

         const reactRoot = createTrackedRoot(toolbarWrapper);
         const fetchFeaturesFn = async () => {
            const response = await fetch('/.well-known/ifixit/flags', {
               credentials: 'include',
            });
            return await response.json();
         };

         const domain = location.hostname.split('.').splice(-2).join('.');

         // Static extensions: non-dev-tools items + metric display buttons
         const extensions = toolbar.extensions
            ? mapDescriptorsToExtensions(toolbar.extensions)
            : [];
         const metrics = getStoredMetrics();
         if (metrics) {
            extensions.push(...createMetricExtensions(metrics));
         }

         // Show toolbar when metrics are available, hide when not
         try {
            if (hasMetrics()) {
               safeLocalStorage.setItem('toolbar_visible', 'true');
            } else {
               safeLocalStorage.removeItem('toolbar_visible');
            }
         } catch {
            // localStorage may be unavailable
         }

         reactRoot.render(
            initializeReactQuery(
               <Toolbar
                  fetchFeaturesFn={fetchFeaturesFn}
                  cookieName="featureOverrides"
                  cookieDomain={'.' + domain}
                  header="PHP Feature Flags"
                  branch={toolbar.branch}
                  extensions={extensions}
               >
                  <CarpenterDevTools descriptors={toolbar.extensions ?? []} />
               </Toolbar>
            )
         );
      }
   );
}
